import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, LogOut, RefreshCw, Package, ArrowLeft, Trash2, Upload, ImageIcon, Film, MessageCircle, Percent } from "lucide-react";
import Seo from "./Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (n) => (typeof n === "number" ? `€${n.toFixed(2).replace(".", ",")}` : "-");

const SOGGETTO_LABELS = [
  ["bimbo", "Bimbo"], ["bimba", "Bimba"], ["cane", "Cane"], ["orsetto", "Orsetto"],
  ["gatto", "Gatto"], ["casa", "Casa"], ["fiore", "Fiore"], ["cuore", "Cuore"],
  ["cuore-amiche", "Cuore Amiche"], ["arcobaleno-bimbo", "Arcobaleno Bimbo"], ["arcobaleno-bimba", "Arcobaleno Bimba"], ["piedino", "Piedino"], ["biberon", "Biberon"],
];

const PRICE_FIELDS = [
  { key: "catena_rosario", label: "Catena Rosario" },
  { key: "catena_perle", label: "Catena Perle" },
  { key: "catena_rolo", label: "Catena Rolò" },
  { key: "soggetto_bonbon", label: "Soggetto (Bon Bon)" },
  { key: "base_bonbon", label: "Base collana Bon Bon" },
  { key: "base_rigido", label: "Bracciale Rigido" },
  { key: "base_morbido", label: "Bracciale Morbido" },
  { key: "base_collana", label: "Collana (Legami)" },
  { key: "lettera_lucida", label: "Lettera Lucida (Legami)" },
  { key: "lettera_zirconata", label: "Lettera Zirconata (Legami)" },
  { key: "iniziale", label: "Iniziale (My Letters)" },
  { key: "spedizione", label: "Costo spedizione" },
  { key: "spedizione_gratis_sopra", label: "Spedizione gratis sopra" },
];

const PRICE_FIELDS_PETIT = SOGGETTO_LABELS.map(([id, l]) => ({ key: `petit_${id}`, label: `Petit · ${l}` }));
const PRICE_FIELDS_MOMENTS = SOGGETTO_LABELS.map(([id, l]) => ({ key: `moments_${id}`, label: `Moments · ${l}` }));

const LEGAMI_LABELS = [
  ["bambino-zirconato", "Bambino zirconato"], ["bambina-zirconata", "Bambina zirconata"],
  ["cuore-zirconato", "Cuore zirconato"], ["zampa-zirconata", "Zampa zirconata"],
  ["quadrifoglio-zirconato", "Quadrifoglio zirconato"], ["stella-zirconata", "Stella zirconata"],
  ["farfalla-zirconata", "Farfalla zirconata"], ["bambino-smaltato-blu", "Bambino smaltato blu"],
  ["bambina-smalto-rosa", "Bambina smalto rosa"], ["cuore-lucido", "Cuore lucido"],
];
const PRICE_FIELDS_LEGAMI = LEGAMI_LABELS.map(([id, l]) => ({ key: `legami_${id}`, label: `Legami · ${l}` }));

const FOTO_GROUPS = [
  {
    title: "Collezioni (card e configuratore)",
    slots: [
      ["collezione_petit", "Petit"], ["collezione_legami", "Legami"], ["collezione_my-letters", "My Letters"],
      ["collezione_bon-bon", "Bon Bon"], ["collezione_moments", "Moments"],
    ],
  },
  {
    title: "Modelli di catena",
    slots: [["catena_rosario", "Rosario"], ["catena_perle", "Perle"], ["catena_rolo", "Rolò"]],
  },
  {
    title: "Soggetti",
    slots: SOGGETTO_LABELS.map(([id, l]) => [`soggetto_${id}`, l]),
  },
  {
    title: "Soggetti — Collezione Legami",
    slots: LEGAMI_LABELS.map(([id, l]) => [`soggetto_legami_${id}`, l]),
  },
  {
    title: "Colore perle (Bon Bon)",
    slots: [["perle_bianche", "Perle Bianche"], ["perle_rosa", "Perle Rosa"], ["perle_azzurre", "Perle Azzurre"]],
  },
  {
    title: "Basi Legami",
    slots: [["base_rigido", "Bracciale Rigido"], ["base_morbido", "Bracciale Morbido"], ["base_collana", "Collana"]],
  },
  {
    title: "Homepage",
    slots: [
      ["hero_main", "Hero principale"], ["hero_secondary", "Hero secondaria"],
      ["manifesto_1", "Manifesto 1"], ["manifesto_2", "Manifesto 2"], ["manifesto_3", "Manifesto 3"],
      ["instagram_1", "Instagram 1"], ["instagram_2", "Instagram 2"], ["instagram_3", "Instagram 3"], ["instagram_4", "Instagram 4"],
    ],
  },
];

const inputCls =
  "w-full px-4 py-2.5 rounded-xl border border-[#F2DBD8] bg-[#FDF8F7] text-sm text-[#37282B] focus:outline-none focus:border-[#E2A3B1]";

const toggleBtnCls =
  "inline-flex items-center gap-2 px-5 py-2.5 border border-[#F2DBD8] rounded-full text-xs uppercase tracking-[0.15em] font-semibold text-[#6B5459] hover:border-[#E2A3B1] hover:text-[#C4758A] transition-colors bg-white";

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-[#A89095] w-32 shrink-0">{label}</span>
      <span className="text-[#37282B] break-words">{value}</span>
    </div>
  );
}

function PriceGrid({ fields, pricing, onChange }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {fields.map((f) => (
        <div key={f.key}>
          <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-[#6B5459] mb-1.5">{f.label}</label>
          <input
            type="number"
            min="0"
            step="0.5"
            value={pricing[f.key] ?? 0}
            onChange={(e) => onChange(f.key, parseFloat(e.target.value) || 0)}
            className={inputCls}
            data-testid={`admin-price-${f.key}`}
          />
        </div>
      ))}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(sessionStorage.getItem("mavi-admin-token") || "");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [showListino, setShowListino] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [showFoto, setShowFoto] = useState(false);
  const [images, setImages] = useState(null);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [showSconti, setShowSconti] = useState(false);
  const [discounts, setDiscounts] = useState(null);
  const [scontoForm, setScontoForm] = useState({ code: "", type: "percent", value: "", expires_at: "" });

  const headers = (t) => ({ Authorization: `Bearer ${t || token}` });

  const fetchOrders = async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: headers(t) });
      setOrders(data.orders);
    } catch {
      sessionStorage.removeItem("mavi-admin-token");
      setToken("");
      setOrders(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = () =>
    axios.get(`${API}/pricing`).then(({ data }) => setPricing(data)).catch(() => {});

  const fetchImages = () =>
    axios.get(`${API}/images`).then(({ data }) => setImages(data || {})).catch(() => {});

  useEffect(() => {
    fetchPricing();
    fetchImages();
    if (token) fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("mavi-admin-token", data.token);
      setToken(data.token);
      fetchOrders(data.token);
    } catch {
      toast.error("Password errata. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("mavi-admin-token");
    setToken("");
    setOrders(null);
    setPassword("");
  };

  const changePwd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: pwdCurrent, new_password: pwdNew },
        { headers: headers() }
      );
      toast.success("Password aggiornata con successo!");
      setShowPwd(false);
      setPwdCurrent("");
      setPwdNew("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il cambio password.");
    }
  };

  const savePricing = async () => {
    try {
      await axios.put(`${API}/admin/pricing`, { pricing }, { headers: headers() });
      toast.success("Listino salvato! Il sito è già aggiornato.");
    } catch {
      toast.error("Errore nel salvataggio del listino.");
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm(`Eliminare definitivamente l'ordine #${id}?`)) return;
    try {
      await axios.delete(`${API}/admin/orders/${id}`, { headers: headers() });
      setOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success(`Ordine #${id} eliminato.`);
    } catch {
      toast.error("Errore durante l'eliminazione.");
    }
  };

  const uploadImage = async (slot, file) => {
    if (!file) return;
    setUploadingSlot(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-image`, fd, { headers: headers() });
      const newImages = { ...(images || {}), [slot]: `${API}/site-image/${data.path}` };
      await axios.put(`${API}/admin/images`, { images: newImages }, { headers: headers() });
      setImages(newImages);
      toast.success("Foto aggiornata! È già visibile sul sito.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento della foto.");
    } finally {
      setUploadingSlot(null);
    }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setUploadingSlot("hero_video");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-video`, fd, { headers: headers() });
      const newImages = { ...(images || {}), hero_video: `${API}/site-image/${data.path}` };
      await axios.put(`${API}/admin/images`, { images: newImages }, { headers: headers() });
      setImages(newImages);
      toast.success("Video caricato! È già visibile in cima al sito.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento del video.");
    } finally {
      setUploadingSlot(null);
    }
  };

  const removeVideo = async () => {
    if (!window.confirm("Rimuovere il video dall'inizio del sito? Tornerà visibile la foto hero.")) return;
    try {
      const newImages = { ...(images || {}) };
      delete newImages.hero_video;
      await axios.put(`${API}/admin/images`, { images: newImages }, { headers: headers() });
      setImages(newImages);
      toast.success("Video rimosso.");
    } catch {
      toast.error("Errore durante la rimozione del video.");
    }
  };

  const waHref = (telefono, nome, id) => {
    let digits = (telefono || "").replace(/\D/g, "");
    if (digits.startsWith("00")) digits = digits.slice(2);
    if (!digits.startsWith("39") && digits.length === 10) digits = `39${digits}`;
    const text = encodeURIComponent(`Ciao ${nome || ""}! Ti scrivo da Ma.Vi Gioielli a proposito del tuo ordine #${id}.`);
    return `https://wa.me/${digits}?text=${text}`;
  };

  const loadDiscounts = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/discounts`, { headers: headers() });
      setDiscounts(data.discounts);
    } catch {
      toast.error("Errore nel caricamento dei codici sconto.");
    }
  };

  const saveDiscount = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/discounts`, {
        code: scontoForm.code,
        type: scontoForm.type,
        value: parseFloat(scontoForm.value),
        expires_at: scontoForm.expires_at || null,
      }, { headers: headers() });
      toast.success("Codice sconto salvato! È già utilizzabile sul sito.");
      setScontoForm({ code: "", type: "percent", value: "", expires_at: "" });
      loadDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio del codice.");
    }
  };

  const toggleDiscount = async (code, active) => {
    try {
      await axios.put(`${API}/admin/discounts/${code}`, { active }, { headers: headers() });
      loadDiscounts();
    } catch {
      toast.error("Errore nell'aggiornamento del codice.");
    }
  };

  const deleteDiscount = async (code) => {
    if (!window.confirm(`Eliminare definitivamente il codice ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/discounts/${code}`, { headers: headers() });
      toast.success("Codice eliminato.");
      loadDiscounts();
    } catch {
      toast.error("Errore nell'eliminazione del codice.");
    }
  };

  const somma = (arr) => arr.reduce((t, o) => t + (o.prezzo_totale || 0), 0);
  const oggiStr = new Date().toDateString();
  const ordiniOggi = (orders || []).filter((o) => o.created_at && new Date(o.created_at).toDateString() === oggiStr);

  return (
    <div className="min-h-screen bg-[#FDF8F7] grain-overlay relative" data-testid="admin-page">
      <Seo title="Area Riservata — Ma.Vi Gioielli" noindex />
      <header className="border-b border-[#F2DBD8] bg-[#FDF8F7]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="admin-logo">
            <img src="/logo.png" alt="Ma.Vi Gioielli" className="h-12 w-auto" />
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C4758A]">Area Riservata</span>
          </a>
          <a href="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#6B5459] hover:text-[#C4758A] transition-colors" data-testid="admin-back-home">
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        {!token ? (
          <motion.form
            onSubmit={login}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto bg-white rounded-3xl border border-[#F2DBD8] p-10 shadow-xl shadow-[#37282B]/5 text-center"
            data-testid="admin-login-form"
          >
            <span className="inline-flex w-14 h-14 rounded-full bg-[#E2A3B1]/15 items-center justify-center mb-5">
              <Lock className="w-6 h-6 text-[#C4758A]" strokeWidth={1.5} />
            </span>
            <h1 className="font-serif-display text-3xl text-[#37282B]">Ordini ricevuti</h1>
            <p className="mt-2 text-sm text-[#6B5459]">Inserisci la password per vedere gli ordini.</p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="mt-6 w-full px-5 py-3.5 rounded-xl border border-[#F2DBD8] bg-[#FDF8F7] text-[#37282B] text-sm focus:outline-none focus:border-[#E2A3B1] focus:ring-2 focus:ring-[#E2A3B1]/25 transition-[border-color,box-shadow] duration-300"
              data-testid="admin-password-input"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="mt-4 w-full px-8 py-3.5 bg-[#37282B] text-[#FDF8F7] text-xs uppercase tracking-[0.2em] font-semibold rounded-full hover:bg-[#E2A3B1] hover:text-[#37282B] transition-colors duration-300 disabled:opacity-60"
              data-testid="admin-login-button"
            >
              {loading ? "Accesso…" : "Accedi"}
            </button>
          </motion.form>
        ) : (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h1 className="font-serif-display text-3xl sm:text-4xl text-[#37282B]" data-testid="admin-title">
                Ordini ricevuti {orders && <span className="text-[#C4758A]">({orders.length})</span>}
              </h1>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => fetchOrders()} data-testid="admin-refresh-button" className={toggleBtnCls}>
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
                </button>
                <button onClick={() => { setShowListino(!showListino); setShowPwd(false); setShowFoto(false); setShowSconti(false); }} data-testid="admin-pricing-toggle" className={toggleBtnCls}>
                  Listino
                </button>
                <button onClick={() => { setShowFoto(!showFoto); setShowPwd(false); setShowListino(false); setShowSconti(false); }} data-testid="admin-foto-toggle" className={toggleBtnCls}>
                  <ImageIcon className="w-3.5 h-3.5" /> Foto
                </button>
                <button onClick={() => { const next = !showSconti; setShowSconti(next); setShowPwd(false); setShowListino(false); setShowFoto(false); if (next) loadDiscounts(); }} data-testid="admin-discounts-toggle" className={toggleBtnCls}>
                  <Percent className="w-3.5 h-3.5" /> Sconti
                </button>
                <button onClick={() => { setShowPwd(!showPwd); setShowListino(false); setShowFoto(false); setShowSconti(false); }} data-testid="admin-password-toggle" className={toggleBtnCls}>
                  Password
                </button>
                <button onClick={logout} data-testid="admin-logout-button" className={toggleBtnCls}>
                  <LogOut className="w-3.5 h-3.5" /> Esci
                </button>
              </div>
            </div>

            {orders && (
              <div className="grid grid-cols-2 gap-4 mb-8" data-testid="admin-summary">
                <div className="bg-white rounded-2xl border border-[#F2DBD8] p-5" data-testid="admin-summary-today">
                  <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#A89095]">Oggi</span>
                  <p className="font-serif-display text-3xl text-[#37282B] mt-1">{ordiniOggi.length} ordini</p>
                  <p className="text-sm text-[#C4758A] font-semibold">{eur(somma(ordiniOggi))}</p>
                </div>
                <div className="bg-white rounded-2xl border border-[#F2DBD8] p-5" data-testid="admin-summary-total">
                  <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#A89095]">Totale</span>
                  <p className="font-serif-display text-3xl text-[#37282B] mt-1">{orders.length} ordini</p>
                  <p className="text-sm text-[#C4758A] font-semibold">{eur(somma(orders))}</p>
                </div>
              </div>
            )}

            {showPwd && (
              <form onSubmit={changePwd} className="mb-8 bg-white rounded-2xl border border-[#F2DBD8] p-6 flex flex-col sm:flex-row gap-3" data-testid="admin-change-password-form">
                <input type="password" placeholder="Password attuale" value={pwdCurrent} onChange={(e) => setPwdCurrent(e.target.value)} required className={`flex-1 ${inputCls}`} data-testid="admin-current-password" />
                <input type="password" placeholder="Nuova password (min. 8 caratteri)" value={pwdNew} onChange={(e) => setPwdNew(e.target.value)} required minLength={8} className={`flex-1 ${inputCls}`} data-testid="admin-new-password" />
                <button type="submit" className="px-6 py-2.5 bg-[#37282B] text-[#FDF8F7] text-xs uppercase tracking-[0.15em] font-semibold rounded-full hover:bg-[#E2A3B1] hover:text-[#37282B] transition-colors" data-testid="admin-save-password-button">
                  Salva
                </button>
              </form>
            )}

            {showSconti && discounts !== null && (
              <div className="mb-10 bg-white rounded-2xl border border-[#F2DBD8] p-6 sm:p-8 space-y-8" data-testid="admin-discounts-panel">
                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C4758A] mb-2">Crea un codice sconto</h3>
                  <p className="text-xs text-[#A89095] mb-4">La cliente lo scrive nel riepilogo prima di pagare. Lo sconto vale solo sul prezzo del gioiello, mai sulla spedizione.</p>
                  <form onSubmit={saveDiscount} className="grid sm:grid-cols-5 gap-3" data-testid="admin-discount-form">
                    <input placeholder="CODICE (es. BENVENUTA10)" value={scontoForm.code} onChange={(e) => setScontoForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} required maxLength={30} className={`${inputCls} uppercase tracking-wider`} data-testid="admin-discount-code" />
                    <select value={scontoForm.type} onChange={(e) => setScontoForm((f) => ({ ...f, type: e.target.value }))} className={inputCls} data-testid="admin-discount-type">
                      <option value="percent">Percentuale %</option>
                      <option value="fixed">Importo fisso €</option>
                    </select>
                    <input type="number" min="0.5" step="0.5" placeholder={scontoForm.type === "percent" ? "Es. 10 = -10%" : "Es. 5 = -€5"} value={scontoForm.value} onChange={(e) => setScontoForm((f) => ({ ...f, value: e.target.value }))} required className={inputCls} data-testid="admin-discount-value" />
                    <input type="date" value={scontoForm.expires_at} onChange={(e) => setScontoForm((f) => ({ ...f, expires_at: e.target.value }))} className={inputCls} title="Scadenza (facoltativa)" data-testid="admin-discount-expiry" />
                    <button type="submit" className="px-6 py-2.5 bg-[#37282B] text-[#FDF8F7] text-xs uppercase tracking-[0.15em] font-semibold rounded-full hover:bg-[#E2A3B1] hover:text-[#37282B] transition-colors" data-testid="admin-discount-save">
                      Crea codice
                    </button>
                  </form>
                  <p className="text-[11px] text-[#A89095] mt-2">La data di scadenza è facoltativa: lasciala vuota per un codice senza scadenza.</p>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C4758A] mb-4">I tuoi codici</h3>
                  {discounts.length === 0 ? (
                    <p className="text-sm text-[#A89095]">Nessun codice creato finora.</p>
                  ) : (
                    <div className="space-y-2">
                      {discounts.map((d) => (
                        <div key={d.code} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#F2DBD8] px-4 py-3" data-testid={`admin-discount-row-${d.code}`}>
                          <span className="font-mono font-semibold text-[#37282B] tracking-widest">{d.code}</span>
                          <span className="text-sm text-[#C4758A] font-semibold">{d.type === "percent" ? `−${d.value}%` : `−${eur(d.value)}`}</span>
                          <span className="text-xs text-[#A89095]">{d.expires_at ? `Scade il ${d.expires_at.split("-").reverse().join("/")}` : "Senza scadenza"}</span>
                          <span className="text-xs text-[#A89095]">{d.uses ? `Usato ${d.uses} volt${d.uses === 1 ? "a" : "e"}` : "Mai usato"}</span>
                          <span className="flex-1" />
                          <button onClick={() => toggleDiscount(d.code, !d.active)} data-testid={`admin-discount-toggle-${d.code}`}
                            className={`px-4 py-1.5 rounded-full text-[11px] uppercase tracking-[0.12em] font-semibold border transition-colors ${d.active ? "border-[#5F7263]/40 text-[#5F7263] hover:bg-[#5F7263] hover:text-white" : "border-[#F2DBD8] text-[#A89095] hover:border-[#C4758A] hover:text-[#C4758A]"}`}>
                            {d.active ? "Attivo" : "Disattivo"}
                          </button>
                          <button onClick={() => deleteDiscount(d.code)} className="p-2 text-[#A89095] hover:text-[#C4758A] transition-colors" aria-label={`Elimina codice ${d.code}`} data-testid={`admin-discount-delete-${d.code}`}>
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {showListino && pricing && (
              <div className="mb-10 bg-white rounded-2xl border border-[#F2DBD8] p-6 sm:p-8 space-y-8" data-testid="admin-pricing-panel">
                <div>
                  <h2 className="font-serif-display text-2xl text-[#37282B] mb-1">Listino prezzi</h2>
                  <p className="text-xs text-[#A89095]">Modifica i prezzi in euro: il sito si aggiorna subito per le nuove clienti.</p>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C4758A] mb-4">Generali</h3>
                  <PriceGrid fields={PRICE_FIELDS} pricing={pricing} onChange={(k, v) => setPricing((p) => ({ ...p, [k]: v }))} />
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C4758A] mb-4">Prezzo di ogni soggetto — Collezione Petit</h3>
                  <PriceGrid fields={PRICE_FIELDS_PETIT} pricing={pricing} onChange={(k, v) => setPricing((p) => ({ ...p, [k]: v }))} />
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C4758A] mb-4">Prezzo di ogni elemento — Collezione Legami</h3>
                  <PriceGrid fields={PRICE_FIELDS_LEGAMI} pricing={pricing} onChange={(k, v) => setPricing((p) => ({ ...p, [k]: v }))} />
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C4758A] mb-4">Prezzo di ogni soggetto — Collezione Moments</h3>
                  <PriceGrid fields={PRICE_FIELDS_MOMENTS} pricing={pricing} onChange={(k, v) => setPricing((p) => ({ ...p, [k]: v }))} />
                </div>
                <button onClick={savePricing} data-testid="admin-save-pricing-button"
                  className="px-8 py-3 bg-[#37282B] text-[#FDF8F7] text-xs uppercase tracking-[0.2em] font-semibold rounded-full hover:bg-[#E2A3B1] hover:text-[#37282B] transition-colors">
                  Salva listino
                </button>
              </div>
            )}

            {showFoto && images !== null && (
              <div className="mb-10 bg-white rounded-2xl border border-[#F2DBD8] p-6 sm:p-8 space-y-8" data-testid="admin-foto-panel">
                <div>
                  <h2 className="font-serif-display text-2xl text-[#37282B] mb-1">Foto del sito</h2>
                  <p className="text-xs text-[#A89095]">Tocca una foto per sostituirla con la tua: il sito si aggiorna subito.</p>
                </div>
                {FOTO_GROUPS.map((g) => (
                  <div key={g.title}>
                    <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C4758A] mb-4">{g.title}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {g.slots.map(([slot, label]) => (
                        <label key={slot} className="group cursor-pointer" data-testid={`admin-foto-${slot}`}>
                          <div className="relative aspect-square rounded-xl overflow-hidden border border-[#F2DBD8] bg-[#FBECEA]">
                            {images?.[slot] ? (
                              <img src={images[slot]} alt={label} className="w-full h-full object-contain" />
                            ) : (
                              <span className="absolute inset-0 flex items-center justify-center text-[#A89095]">
                                <ImageIcon className="w-6 h-6" strokeWidth={1.5} />
                              </span>
                            )}
                            <span className="absolute inset-0 bg-[#37282B]/0 group-hover:bg-[#37282B]/35 transition-colors flex items-center justify-center">
                              <Upload className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </span>
                          </div>
                          <span className="block mt-2 text-xs font-medium text-[#37282B] text-center">
                            {uploadingSlot === slot ? "Caricamento…" : label}
                          </span>
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(slot, e.target.files?.[0])} />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C4758A] mb-4">Video in cima al sito (Hero)</h3>
                  <p className="text-xs text-[#A89095] mb-4">Carica un video MP4 (consigliato: breve, max 100 MB): partirà in automatico in loop, senza audio, al posto della foto hero. Su telefono sarà visibile sotto il titolo.</p>
                  <div className="flex flex-wrap items-start gap-5">
                    <label className="group cursor-pointer w-48" data-testid="admin-video-hero">
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden border border-[#F2DBD8] bg-[#FBECEA]">
                        {images?.hero_video ? (
                          <video src={images.hero_video} autoPlay muted loop playsInline className="w-full h-full object-cover" />
                        ) : (
                          <span className="absolute inset-0 flex items-center justify-center text-[#A89095]">
                            <Film className="w-6 h-6" strokeWidth={1.5} />
                          </span>
                        )}
                        <span className="absolute inset-0 bg-[#37282B]/0 group-hover:bg-[#37282B]/35 transition-colors flex items-center justify-center">
                          <Upload className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </span>
                      </div>
                      <span className="block mt-2 text-xs font-medium text-[#37282B] text-center">
                        {uploadingSlot === "hero_video" ? "Caricamento video…" : images?.hero_video ? "Sostituisci video" : "Carica video"}
                      </span>
                      <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => uploadVideo(e.target.files?.[0])} />
                    </label>
                    {images?.hero_video && (
                      <button onClick={removeVideo} data-testid="admin-video-remove-button"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-[#F2DBD8] rounded-full text-xs uppercase tracking-[0.12em] font-semibold text-[#C4758A] hover:border-[#C4758A] transition-colors bg-white">
                        <Trash2 className="w-3.5 h-3.5" /> Rimuovi video
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {loading && !orders && <p className="text-[#A89095] text-sm">Caricamento ordini…</p>}
            {orders && orders.length === 0 && (
              <div className="text-center py-20 text-[#A89095]" data-testid="admin-empty">
                <Package className="w-10 h-10 mx-auto mb-4 text-[#E2A3B1]" strokeWidth={1.5} />
                Nessun ordine ancora ricevuto.
              </div>
            )}

            <div className="space-y-5" data-testid="admin-orders-list">
              {orders?.map((o) => {
                const pagato = (o.pagamento || "").toLowerCase().includes("pagato");
                return (
                  <motion.article
                    key={o.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl border border-[#F2DBD8] p-6 shadow-sm"
                    data-testid={`admin-order-${o.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        {o.collezione_image && (
                          <img src={o.collezione_image} alt={o.collezione} className="w-16 h-16 rounded-xl object-cover border border-[#F2DBD8]" data-testid={`admin-order-image-${o.id}`} />
                        )}
                        <div>
                          <span className="font-serif-display text-xl text-[#37282B]">#{o.id}</span>
                          <span className="block text-sm text-[#C4758A] font-medium mt-0.5">{o.collezione}</span>
                          <span className="block text-xs text-[#A89095] mt-0.5">
                            {o.created_at ? new Date(o.created_at).toLocaleString("it-IT") : ""}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex items-start gap-3">
                        <div>
                          <span className="font-serif-display text-2xl text-[#37282B]" data-testid={`admin-order-total-${o.id}`}>{eur(o.prezzo_totale)}</span>
                          <span
                            className={`block mt-1 text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-1 rounded-full ${
                              pagato ? "bg-[#8A9A86]/15 text-[#5F7263]" : "bg-[#E2A3B1]/15 text-[#C4758A]"
                            }`}
                            data-testid={`admin-order-payment-${o.id}`}
                          >
                            {o.pagamento || "Da concordare"}
                          </span>
                        </div>
                        {o.cliente_telefono && (
                          <a
                            href={waHref(o.cliente_telefono, o.cliente_nome, o.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2.5 rounded-full border border-[#F2DBD8] text-[#A89095] hover:text-[#25D366] hover:border-[#25D366]/50 transition-colors"
                            aria-label={`Contatta la cliente dell'ordine ${o.id} su WhatsApp`}
                            title="Contatta su WhatsApp"
                            data-testid={`admin-order-whatsapp-${o.id}`}
                          >
                            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                          </a>
                        )}
                        <button
                          onClick={() => deleteOrder(o.id)}
                          className="p-2.5 rounded-full border border-[#F2DBD8] text-[#A89095] hover:text-red-500 hover:border-red-200 transition-colors"
                          aria-label={`Elimina ordine ${o.id}`}
                          data-testid={`admin-order-delete-${o.id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 mt-5 pt-5 border-t border-[#F2DBD8]">
                      <Row label="Catena" value={o.catena} />
                      <Row label="Base" value={o.base} />
                      <Row label="Elementi" value={o.elementi} />
                      <Row label="Perle" value={o.perle} />
                      <Row label="Galvanica" value={o.galvanica} />
                      <Row label="Lettere" value={o.lettere} />
                      <Row label="Iniziali" value={o.iniziali?.length ? o.iniziali.join(", ") : ""} />
                      {o.sconto_importo > 0 && <Row label="Sconto" value={`−${eur(o.sconto_importo)} (codice ${o.codice_sconto})`} />}
                      <Row label="Spedizione" value={o.spedizione_gratuita ? "Gratuita" : "A pagamento"} />
                    </div>

                    {o.soggetti?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-3">
                        {o.soggetti.map((s, i) => (
                          <span key={i} className="inline-flex items-center gap-2.5 bg-[#FDF8F7] border border-[#F2DBD8] rounded-xl px-3 py-2" data-testid={`admin-order-soggetto-${o.id}-${i}`}>
                            {s.image && <img src={s.image} alt={s.tipo} className="w-9 h-9 rounded-lg object-cover" />}
                            <span className="text-xs text-[#37282B] font-medium">
                              {s.tipo}
                              {s.incisione && <em className="block text-[#A89095] font-normal">“{s.incisione}”</em>}
                            </span>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 mt-4 pt-4 border-t border-[#F2DBD8]">
                      <Row label="Cliente" value={`${o.cliente_nome || ""} ${o.cliente_cognome || ""}`.trim()} />
                      <Row label="Email" value={o.cliente_email} />
                      <Row label="Telefono" value={o.cliente_telefono} />
                      <Row label="Indirizzo" value={o.cliente_indirizzo} />
                      <Row label="Codice fiscale" value={o.codice_fiscale} />
                      <Row label="Note" value={o.note} />
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
